import Controller, { OnBeforeSubmit, OnCancel, OnClick, OnKeyUp } from '@philips-internal/oswald-ui-core/lib/client';
import { Inject } from '@odinjs/odin';
import BaseController from '../../BaseController';
import VitalSignService from '../services/vital-sign-service';
import { SimpleDialog } from '../../dialog-helper';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { AbortableSubmitEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ButtonAPI, ModalService, PanelAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ApplicationRoute } from '../../../constants/routes';

import './vital-sign-inactivate-controller.css';

const MAX_LENGTH_JUSTIFICATION = 255;

@Controller({ component: 'layout-vital-sign-inactivation', fragment: 'vital-sign-inactivate' })
class VitalSignInactivationController extends BaseController {

  @Inject('button-submit-inactivate')
  declare buttonInactivate: ButtonAPI;

  @Inject('justification-text-area')
  declare justificationTextArea: TextAreaAPI;

  @Inject('panel-loader')
  declare panelLoader: PanelAPI;

  @Inject('SubmittableFragmentContext')
  declare submittableFragmentContext: SubmittableFragmentContext;

  close(e: SubmitEvent): void {
    const { record } = e;
    if(this.submittableFragmentContext) {
      this.submittableFragmentContext.submit(new SubmitEvent({ record }));
    }
    ModalService.close('vital-sign-inactivate');
  }

  justificationTextIsEmpty(): boolean {
    const justificationTextArea = this.justificationTextArea.getValue().trim();
    return !justificationTextArea || justificationTextArea.length === 0;
  }

  justificationTextLength(): number {
    const justificationTextArea = this.justificationTextArea.getValue().trim();
    return justificationTextArea ? justificationTextArea.length : 0;
  }

  goVitalSignList(e: SubmitEvent): void {
    this.close(e);

    applicationContext.redirect(ApplicationRoute.ELECTRONIC_PATIENT_CHART +
      ApplicationRoute.PATIENT_LIST +
      ApplicationRoute.VITAL_SIGN_LIST);
  }

  @OnKeyUp({ target: 'justification-text-area' })
  onJustificationTextKeyUp(): void {
    this.buttonInactivate.setEnabled(!this.justificationTextIsEmpty());
  }

  @OnCancel({ target: 'justification-form' })
  onClickCancel(e: SubmitEvent): void {
    this.close(e);
  }

  @OnClick({ target: 'btn-message-ok' })
  onClickOk(e: SubmitEvent): void {
    this.goVitalSignList(e);
  }

  @OnBeforeSubmit({ target: 'justification-form' })
  onClickInactivateSubmit(e: AbortableSubmitEvent): void {
    e.abort(this.handleInactivateClick(e));
  }

  async handleInactivateClick(e: AbortableSubmitEvent): Promise<boolean> {
    const { vitalSignId, encounterId } = this.historyService.getCurrentRouteParams() as { encounterId: number, vitalSignId: number; };

    if (this.justificationTextLength() <= 0) {
      return false;
    } if (this.justificationTextLength() > MAX_LENGTH_JUSTIFICATION) {
      return false;
    }

    const dsJustificativa = this.justificationTextArea.getValue();

    const vsService = new VitalSignService(this);
    let inactivated = false;

    try {
      this.panelLoader.setVisible(true);

      inactivated = await vsService.inactivate(encounterId, vitalSignId, dsJustificativa);

      if (inactivated) {
        SimpleDialog.showMessageDialog(
          'title.disable-record',
          'patient-evolution.successfully-inactivated-message',
          () => { this.goVitalSignList(e) }
        );
      } else {
        SimpleDialog.showMessageDialog(
          'patient-evolution.error-message',
          '',
          () => { this.close(e) }
        );
      }
    } catch (error: any) {

      SimpleDialog.showMessageDialog(
        'patient-evolution.error-message',
        error?.body as string,
        () => { this.close(e) }
      );
    } finally {
      this.panelLoader.setVisible(false);
    }

    return inactivated;
  }
}

export default VitalSignInactivationController;
